import { Checkbox } from '@/components';
import { ApiClientError, type ReleaseEnvironment } from '@uchat-team-management/shared';
import { For, Match, Show, Switch } from 'solid-js';
import { useFadTesterGroups } from './hooks';

/**
 * Options-only tester-group picker (UCH-513): checkbox list fed live from
 * Firebase App Distribution — no free text. A selected alias missing from FAD
 * (deleted group / stale default) still renders, flagged, so it can be removed.
 */
export const FadGroupPicker = (props: {
  productId: string;
  environment: ReleaseEnvironment;
  value: string[];
  onChange: (aliases: string[]) => void;
  disabled?: boolean;
  label?: string;
}) => {
  const groups = useFadTesterGroups(() => ({
    productId: props.productId,
    environment: props.environment,
  }));

  const options = () => groups.data?.data.groups ?? [];

  const toggle = (alias: string, checked: boolean) =>
    props.onChange(checked ? [...props.value, alias] : props.value.filter((a) => a !== alias));

  const missing = () => {
    const known = new Set(options().map((g) => g.alias));
    return props.value.filter((a) => !known.has(a));
  };

  const errorText = (err: unknown): string =>
    err instanceof ApiClientError && err.error.code === 'INTEGRATION_NOT_CONFIGURED'
      ? 'Configure the Firebase app ids first'
      : 'Could not load tester groups';

  return (
    <div class="flex flex-col gap-1 text-sm">
      <Show when={props.label}>
        <span class="font-medium text-neutral-700 dark:text-neutral-300">{props.label}</span>
      </Show>
      <Switch>
        <Match when={groups.isPending}>
          <p class="text-xs text-neutral-500">Loading tester groups…</p>
        </Match>
        <Match when={groups.isError}>
          <p class="text-xs text-red-600">{errorText(groups.error)}</p>
        </Match>
        <Match when={groups.data}>
          {/* Wrap so long group lists flow into rows instead of one tall stack. */}
          <div class="flex flex-wrap gap-x-4 gap-y-1.5">
            <For each={options()}>
              {(g) => (
                <Checkbox
                  checked={props.value.includes(g.alias)}
                  onChange={(checked) => toggle(g.alias, checked)}
                  disabled={props.disabled}
                  label={
                    <span>
                      {g.displayName}
                      <Show when={g.displayName !== g.alias}>
                        <span class="ml-1 text-xs text-neutral-500">({g.alias})</span>
                      </Show>
                    </span>
                  }
                />
              )}
            </For>
            <For each={missing()}>
              {(alias) => (
                <Checkbox
                  checked
                  onChange={(checked) => toggle(alias, checked)}
                  disabled={props.disabled}
                  label={
                    <span>
                      {alias}
                      <span class="ml-1 text-xs text-amber-600">not found on Firebase</span>
                    </span>
                  }
                />
              )}
            </For>
            <Show when={options().length === 0 && missing().length === 0}>
              <p class="text-xs text-neutral-500">
                No tester groups found — press Sync on the product page
              </p>
            </Show>
          </div>
        </Match>
      </Switch>
    </div>
  );
};
